// React, Router, Permissions and Services
import { useAbility } from "@casl/react";
import { AbilityContext, Can } from "Can";
import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import partCodesService from "services/part-code-service";
import { AuthContext } from "context"
import formatPartCode from "helper/formatPartCode";

// Material Dashboard and MUI components
import DeleteForeverTwoToneIcon from '@mui/icons-material/DeleteForeverTwoTone';
import VisibilityTwoToneIcon from '@mui/icons-material/VisibilityTwoTone';
import SettingsBackupRestoreTwoToneIcon from "@mui/icons-material/SettingsBackupRestoreTwoTone";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";
import ConfirmDialog from "e4acomponents/ConfirmDialog";

export default function ArchivedPartCodes() {
    // Data
    const [parts, setParts] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Confirm Dialog states and props
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogProps, setDialogProps] = useState({
        title: "",
        prompt: "",
        onClose: () => { }
    });
    const restorePrompt = "Are you sure you want to restore this part?";
    const restoreTitle = "Restore Part";
    const deletePrompt = "Are you sure you want to permanently delete this part? This action cannot be undone.";
    const deleteTitle = "Permanently Delete Part";

    const navigate = useNavigate();

    // Permissions
    const ability = useAbility(AbilityContext);

    const authContext = useContext(AuthContext);

    // Fetch part codes
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const response = await partCodesService.getPartsByStatus("archived");
                setParts(response.data.attributes);
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Update table's rows at the slightest change
    useEffect(() => {
        setTableData(getRows(parts));
    }, [parts]);

    // Handler to navigate to the specific view page of the part code
    const goToViewPage = (id) => {
        navigate(`/part-code-management/view-part-code/${id}`);
    };

    // Format data for DataTable
    const getRows = (info) => {
        let updatedInfo = info
            // Decomment this line to filter partcodes by status .filter(e => (e.status === "Approved"))
            .map((e) => {
                return {
                    categoryCode: formatPartCode(e),
                    id: e._id,
                    manufacturerPartNum: e.mpn ? e.mpn[e.preferred_mpn] : e.manufacturer_part_number,
                    PartCategory: e.partCategory.description,
                    description: e.has_attributes_filled ? description(e) : e.description,
                    statusChip: <Chip label={"Archived"} color={statusColor(e.status)} variant="outlined" />,
                    status: e.status,
                    partCatStatus: e.partCategory.status,
                };
            });
        return updatedInfo
    };

    // Helper function to determine the color of the status chip
    const statusColor = (status) => {
        switch (status) {
            case "Archived":
                return "warning";
            default:
                return "default";
        }
    }

    // Helper function to generate the detailed description based on the attributes of the partcode
    const description = (partCode) => {
        const categoryCode = partCode.partCategory.categoryCode;
        return partCode.description.startsWith(categoryCode)
            ? partCode.description
            : `${categoryCode} ${partCode.description}`;
    };

    // Behaviour when clicking the revive part button
    const clickReviveHandler = async (id) => {
        try {
            const payload = {
                data: {
                    attributes: {
                        status: "Pending Approval"
                    },
                    logs: {
                        user: await authContext.getCurrentUser(),
                        comment: "",
                    }
                }
            }
            await partCodesService.editStatus(id, payload);
            // Remove the revived part from the table
            setParts((prev) => prev.filter((item) => item._id !== id));
        } catch (error) {
            console.log(error);
        }
    }

    // Behaviour when clicking the delete part button
    const clickDeleteHandler = async (id) => {
        try {
            const userId = await authContext.getCurrentUser();
            await partCodesService.deletePart(id, userId);
            // Remove the deleted part from the table
            setParts((prev) => prev.filter((item) => item._id !== id));
        } catch (error) {
            console.log(error);
        }
    };

    const closeDialog = async (response, title, id) => {
        // Handle dialog close response if needed
        if (response) {
            if (title === restoreTitle) {
                await clickReviveHandler(id);
            } else if (title === deleteTitle) {
                await clickDeleteHandler(id);
            }
        }
        setOpenDialog(false);
    }
    const handleOpenDialog = (title, prompt, id) => {
        setDialogProps({
            title: title,
            prompt: prompt,
            id: id,
            onClose: closeDialog
        })
        setOpenDialog(true);
    }



    // Array containing the dataTable's headers and rows
    const dataTableData = {
        columns: [
            {
                Header: "Part Code",
                accessor: "categoryCode",
                width: "20%",
                Cell: (info) => {
                    return (
                        <MDTypography
                            component="a"
                            href="#"
                            variant="button"
                            fontWeight="medium"
                            onClick={() => goToViewPage(info.cell.row.original.id)}
                            sx={{ cursor: "pointer", textDecoration: "none", color: "#344767" }}
                        >
                            {info.cell.value}
                        </MDTypography>
                    );
                }
            },
            { Header: "Part Category", accessor: "PartCategory", width: "12%" },
            { Header: "Manufacturer Part Number", accessor: "manufacturerPartNum", width: "13%" },
            { Header: "Description", accessor: "description", width: "25%" },
            {
                Header: "Status",
                accessor: "status",
                width: "15%",
                Cell: ({ row }) => row.original.statusChip
            },
            {
                Header: "Actions",
                disableSortBy: true,
                accessor: "actions",
                width: "15%",
                Cell: (info) => {
                    const disableRestore = info.cell.row.original.partCatStatus.toLowerCase() === "archived";
                    return (

                        <MDBox display="flex" alignItems="center">
                            {/* Delete button */}
                            <Can I="Delete" this={"PartCode"}>
                                <Tooltip
                                    title="Permanently Delete Part"
                                    onClick={() => handleOpenDialog(
                                        deleteTitle,
                                        deletePrompt,
                                        info.cell.row.original.id
                                    )}
                                >
                                    <IconButton>
                                        <DeleteForeverTwoToneIcon color="error" />
                                    </IconButton>
                                </Tooltip>
                            </Can>

                            {/* Restore Button */}
                            <Tooltip
                                title="Restore Part"
                                onClick={() => handleOpenDialog(
                                    restoreTitle,
                                    restorePrompt,
                                    info.cell.row.original.id
                                )}
                                disabled={disableRestore}
                            >
                                <IconButton >
                                    <SettingsBackupRestoreTwoToneIcon color={disableRestore ? "disabled" : "success"} />
                                </IconButton>
                            </Tooltip>

                            {/*View Part Code Button */}
                            < Tooltip
                                title="View Part"
                                onClick={() => navigate(`/part-code-management/view-part-code/${info.cell.row.original.id}`)}
                            >
                                <IconButton>
                                    <VisibilityTwoToneIcon color="info" />
                                </IconButton>
                            </Tooltip>
                        </MDBox >
                    );
                },
            },
        ],
        rows: tableData
    };
    return (
        <DashboardLayout>

            <ConfirmDialog
                open={openDialog}
                props={dialogProps}
            />

            <DashboardNavbar />

            <MDBox pt={6} pb={3}>

                {/* Main Card */}
                <Card>

                    {/* Title and Create Button */}
                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">
                        <MDTypography variant="h5" fontWeight="medium">
                            Archived Part Codes
                        </MDTypography>
                    </MDBox>

                    {/* Data Table */}
                    <MDBox>
                        <DataTable
                            table={dataTableData}
                            canSearch
                            isLoading={isLoading}
                            tableId="archived-part-codes-table"
                        />
                    </MDBox>

                </Card>

            </MDBox>

            {/* Footer */}
            <MDBox>

                <Footer />

            </MDBox>

        </DashboardLayout>

    );

}